/**
 * DualAnalytics — one facade that fans every event out to two independent
 * sinks: GA4 (reporting, blockable) and the Source Digital pixel
 * (blocker-resistant firehose). Modeled on Source Digital's own DualTelemetry.
 *
 * Guarantees:
 *  - One sink failing never affects the other or the app (each call is
 *    isolated + swallowed).
 *  - A shared `context` (session, user tier, platform) is merged into every
 *    event so Redash can slice without callers repeating themselves.
 *  - Sensitive URL params / keys are redacted before anything is sent.
 */

import { initGa, gaPageView, gaEvent } from './gaSink';
import { initPixel, pixelEvent } from './pixelSink';
import { redactUrl, redactPath, redactParams } from './redact';
import { consoleLoggingEnabled, analyticsEnabled } from './config';
import { touchSession } from './sessionManager';
import { EVENTS, EVENT_POLICY, hashToUnit } from './events';

class DualAnalytics {
  constructor() {
    this.started = false;
    this.context = {};
    this.sessionId = null;
    this._pendingAppOpen = false;
    this._flushing = false;
  }

  /** Initialize both sinks once. */
  init() {
    if (this.started) {
      return;
    }
    this.started = true;
    initGa();
    initPixel();
    this._log('init', { enabled: analyticsEnabled() });
  }

  /**
   * Resolve the persistent session before every event so `session_id` survives
   * redirects/reloads/tabs (GA4 semantics). Queues a single `app_open` (the
   * session-open event) to be flushed just before the triggering event. A
   * brand-new device / first-ever open is simply `app_open` with
   * `session_number === 1` — no separate first_visit event.
   */
  _ensureSession() {
    const { sessionId, sessionNumber, isNewSession } = touchSession();
    this.sessionId = sessionId;
    this.context.session_id = sessionId;
    this.context.session_number = sessionNumber;
    if (isNewSession) {
      this._pendingAppOpen = true;
    }
  }

  /**
   * Emit the queued `app_open` exactly once, ahead of the event that triggered
   * it. Pixel-only routing comes from EVENT_POLICY (GA4 has its own native
   * session lifecycle). `session_number` (in the shared context) carries the
   * new-vs-returning signal.
   * @param {string} currentName - the event about to be sent (so app_open never
   *   recursively flushes itself)
   */
  _flushLifecycle(currentName) {
    if (this._flushing) {
      return;
    }
    this._flushing = true;
    try {
      if (this._pendingAppOpen && currentName !== EVENTS.APP_OPEN) {
        this._pendingAppOpen = false;
        this._emit(EVENTS.APP_OPEN, {});
      }
    } finally {
      this._flushing = false;
    }
  }

  /**
   * Raw sink fan-out for a named event (no session/lifecycle handling).
   * Routing precedence: an explicit call-site sink toggle wins, else the
   * event's EVENT_POLICY default, else both sinks. Sampled events (a policy
   * `sample` fraction) are dropped for sessions that hash outside the rate.
   * @param {string} name
   * @param {Object} [params]
   * @param {Object} [sinks] - call-site overrides `{ ga, pixel }`
   */
  _emit(name, params = {}, sinks = {}) {
    const policy = EVENT_POLICY[name] || {};
    // Deterministic per-session sampling (default rate 1.0 → always keep).
    if (policy.sample != null && policy.sample < 1) {
      if (hashToUnit(this.sessionId || '') >= policy.sample) {
        return;
      }
    }
    const ga = sinks.ga != null ? sinks.ga : policy.ga != null ? policy.ga : true;
    const pixel = sinks.pixel != null ? sinks.pixel : policy.pixel != null ? policy.pixel : true;
    const safe = redactParams(params);
    if (ga) {
      this._safe(() => gaEvent(name, safe));
    }
    if (pixel) {
      this._safe(() => pixelEvent(name, { ...this.context, ...safe }));
    }
    this._log(name, safe);
  }

  /**
   * Merge fields into the context sent with every subsequent event
   * (e.g. user auth state, subscription tier). Never include PII.
   * @param {Object} ctx
   */
  setContext(ctx = {}) {
    this.context = { ...this.context, ...redactParams(ctx) };
  }

  /**
   * Track a page view (SPA route change).
   * @param {string} [url] - absolute URL (defaults to current location)
   * @param {string} [title]
   * @param {Object} [sinks] - per-sink toggles; e.g. `{ pixel: false }` on
   *   watch routes, where the pixel gets a `video_view` instead but GA4 still
   *   needs its page_view for SPA reporting.
   */
  pageView(url, title, { ga = true, pixel = true } = {}) {
    if (!this.started) {
      this.init();
    }
    this._ensureSession();
    this._flushLifecycle('page_view');
    const path = redactPath(url);
    const location = redactUrl(url);
    if (ga) {
      this._safe(() => gaPageView(path, title));
    }
    if (pixel) {
      this._safe(() =>
        pixelEvent('page_view', {
          ...this.context,
          page_location: location,
          page_path: path,
          page_title: title,
        }),
      );
    }
    this._log('page_view', { path, ga, pixel });
  }

  /**
   * Track an event. Fans to both sinks by default; pass `{ ga: false }` or
   * `{ pixel: false }` to target one (e.g. `video_view` is pixel-only — GA4
   * can't treat a custom event as a page view).
   * @param {string} name - GA4-style event name
   * @param {Object} [params]
   * @param {Object} [sinks] - per-sink toggles `{ ga, pixel }` (default both true)
   */
  track(name, params = {}, sinks = {}) {
    if (!name) {
      return;
    }
    if (!this.started) {
      this.init();
    }
    this._ensureSession();
    this._flushLifecycle(name);
    this._emit(name, params, sinks);
  }

  /** Run a sink call in isolation — one sink's failure never affects the app. */
  _safe(fn) {
    try {
      fn();
    } catch (e) {
      // swallow — analytics must never break the app
    }
  }

  _log(label, data) {
    if (!consoleLoggingEnabled()) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`%c[analytics] ${label}`, 'color:#60a5fa', data || {});
  }
}

export default DualAnalytics;
